import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuthUnified } from '@/hooks/shared/useAuthUnified';
import { useDomain } from '@/shared/contexts/DomainProvider';
import { LogoWithDomainSwitcher } from '@/shared/components/layout/LogoWithDomainSwitcher';
import {
  LayoutDashboard,
  Settings,
  CheckSquare,
  Users,
  FileCheck,
  Archive,
  ChevronRight,
  ChevronLeft,
  X
} from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ForwardRefExoticComponent<any>;
  step: number | null;
  shortName?: string;
}

/**
 * Sidebar component - Main navigation sidebar with role-based menu items
 * Responsive design with mobile support
 */
const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, isCollapsed, onToggleCollapse }) => {
  const { hasGroupRouteAccess } = useAuthUnified();
  const { currentDomain } = useDomain();
  const location = useLocation();

  // Navigation items based on user roles and BI process steps
  const getNavigationItems = (): NavigationItem[] => {
    const dashboardItem: NavigationItem[] = [
      {
        name: 'Dashboard',
        href: `/${currentDomain}/dashboard`,
        icon: LayoutDashboard,
        step: null
      }
    ];

    // BI Process Steps (1-5)
    const processSteps: NavigationItem[] = [
      {
        name: 'Step 1: Monthly Cycle Setup',
        href: `/${currentDomain}/setup`,
        icon: Settings,
        step: 1,
        shortName: 'Setup'
      },
      {
        name: 'Step 2: Individual Data Collection',
        href: `/${currentDomain}/my-tasks`,
        icon: CheckSquare,
        step: 2,
        shortName: 'Individual Tasks'
      },
      {
        name: 'Step 3: Team Data Collection',
        href: `/${currentDomain}/team-tasks`,
        icon: Users,
        step: 3,
        shortName: 'Team Tasks'
      },
      {
        name: 'Step 4: Review & QA',
        href: `/${currentDomain}/review`,
        icon: FileCheck,
        step: 4,
        shortName: 'Review & QA'
      },
      {
        name: 'Step 5: Data Archive & Controls',
        href: `/${currentDomain}/archive`,
        icon: Archive,
        step: 5,
        shortName: 'Archive'
      }
    ];

    let items: NavigationItem[] = [...dashboardItem];

    const allowedSteps = processSteps.filter(step => hasGroupRouteAccess(step.href));
    items = [...items, ...allowedSteps];

    return items;
  };

  const navigationItems = getNavigationItems();

  const isActiveRoute = (href: string) => {
    if (href === `/${currentDomain}/dashboard`) {
      return location.pathname === href || location.pathname === `/${currentDomain}` || location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar - Floating Container */}
      <div
        className={`fixed z-50 bg-white transform transition-all duration-300 ease-in-out
          lg:left-4 lg:top-4 lg:bottom-4 lg:rounded-2xl lg:shadow-2xl lg:translate-x-0
          inset-y-0 left-0 shadow-lg
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isCollapsed ? 'w-20' : 'w-72'}
        `}
      >
        <div className="flex flex-col h-full lg:rounded-2xl overflow-hidden">
          {/* Sidebar header - Mobile close button only */}
          <div className="flex items-center justify-end p-4 border-b border-gray-200 lg:hidden">
            <button
              onClick={onClose}
              className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Logo with integrated Domain Switcher */}
          <LogoWithDomainSwitcher isCollapsed={isCollapsed} />

          {/* Navigation */}
          <nav className="flex-1 px-4 py-5 space-y-1 overflow-y-auto">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = isActiveRoute(item.href);
              
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={() => {
                    // Close mobile menu when navigating
                    if (window.innerWidth < 1024) {
                      onClose();
                    }
                  }}
                  className={`group flex items-center text-sm font-medium rounded-lg transition-all ${
                    isCollapsed ? 'px-3 py-3 justify-center' : 'px-3 py-2'
                  } ${
                    isActive
                      ? 'bg-gradient-to-r from-[#5b3d72] to-[#704a8b] text-white shadow-md'
                      : 'text-gray-600 hover:bg-[#ebe4f1]/50 hover:text-[#311c3b]'
                  }`}
                  title={item.name}
                >
                  <Icon
                    className={`h-5 w-5 transition-colors ${
                      isCollapsed ? '' : 'mr-3'
                    } ${
                      isActive ? 'text-white' : 'text-gray-400 group-hover:text-[#311c3b]'
                    }`}
                  />
                  {!isCollapsed && (
                    <span className="flex-1">
                      {item.shortName || item.name}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </nav>

        </div>

        {/* Collapse toggle button - Desktop only, positioned outside overflow container */}
        <button
          onClick={onToggleCollapse}
          className="hidden lg:flex absolute -right-3 top-[68px] z-[60] w-6 h-6 items-center justify-center bg-white rounded-full border border-gray-200 shadow-md hover:shadow-lg hover:border-gray-300 hover:scale-110 transition-all cursor-pointer"
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          type="button"
        >
          {isCollapsed ? (
            <ChevronRight className="h-3.5 w-3.5 text-gray-600" />
          ) : (
            <ChevronLeft className="h-3.5 w-3.5 text-gray-600" />
          )}
        </button>
      </div>
    </>
  );
};


export default Sidebar;
